// Vendor libraries
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

// Interfaces
import { BackgroundJobProgressUpdate } from 'NotificationsApp/ts/interfaces/background-job.interface';

// Utils
import { YChartsSentryUtils } from 'Angular/services/sentry/sentry-utils';

@Injectable({
    providedIn: 'root',
})
export class BackgroundJobEventsService {
    private _eventSource: EventSource | null = null;

    private _retryCount = 0;

    private _maxRetries = 5;

    private _baseRetryDelay = 1000;

    private _retryTimeout: ReturnType<typeof setTimeout> | null = null;

    private readonly ngZone = inject(NgZone);

    readonly jobsInProgress = signal<{ [id: string]: BackgroundJobProgressUpdate }>({});

    readonly lastTerminalJob = signal<BackgroundJobProgressUpdate | null>(null);

    // Set by an editor page while it's showing its own in-editor Quick Extract loading state for this job,
    // so the global side menu doesn't auto-open the notifications panel for the same completion.
    readonly editorWatchedJobId = signal<string | null>(null);

    private readonly _lastTerminalJob$ = new Subject<BackgroundJobProgressUpdate>();

    readonly lastTerminalJob$: Observable<BackgroundJobProgressUpdate> = this._lastTerminalJob$.asObservable();

    stream(): void {
        this._retryCount = 0;
        this.connect();
    }

    private connect(): void {
        const baseUrl =
            window.location.hostname === 'localhost' ? 'http://localhost:3003' : process.env.AI_PROXY_BASE_URL;

        this._eventSource = new EventSource(`${baseUrl}/jobs_progress/${window.USER.userId}`, {
            withCredentials: true,
        });

        this._eventSource.onopen = () => {
            this._retryCount = 0;
        };

        this._eventSource.onmessage = (event: MessageEvent) => this.handleProgressUpdate(event);

        this._eventSource.onerror = (e) => {
            this.closeStream();

            if (this._retryCount < this._maxRetries) {
                const delay = this._baseRetryDelay * 2 ** this._retryCount;
                this._retryCount += 1;
                this._retryTimeout = setTimeout(() => {
                    this._retryTimeout = null;
                    this.connect();
                }, delay);
            } else {
                YChartsSentryUtils.captureException(e);
                this._retryCount = 0;
            }
        };
    }

    isConnected(): boolean {
        return this._eventSource !== null;
    }

    private handleProgressUpdate(event: MessageEvent): void {
        try {
            const update: BackgroundJobProgressUpdate = JSON.parse(event.data);
            this.ngZone.run(() => {
                if (update.status === 'completed' || update.status === 'failed') {
                    this.jobsInProgress.update(({ [update.job_id]: _, ...rest }) => rest);
                    this.lastTerminalJob.set(update);
                    this._lastTerminalJob$.next(update);
                } else {
                    this.jobsInProgress.update((jobs) => {
                        const { [update.job_id]: _, ...rest } = jobs;
                        return { [update.job_id]: update, ...rest };
                    });
                }
            });
        } catch (e) {
            YChartsSentryUtils.captureException(e);
        }
    }

    closeStream(): void {
        if (this._retryTimeout !== null) {
            clearTimeout(this._retryTimeout);
            this._retryTimeout = null;
        }
        if (this._eventSource !== null) {
            this._eventSource.close();
            this._eventSource = null;
        }
    }
}
